import { ErrorEvent, EventHint, TransactionEvent } from '@sentry/core';
import { getGlobalScope } from '@sentry/react';
import { EnvironmentEnum, isE2eTest, isKiosk } from 'utils/platform';
import {
  E2E_TEST_ENVIRONMENT,
  isVerboseSentry,
  ReplaysOnErrorSampleRateEnum,
  ReplaysSessionSampleRateEnum,
  TracesSampleRateEnum,
  unsupportedLocales
} from 'utils/sentry';

const getExceptionMaybe = (hint: EventHint) => {
  const exception = hint.originalException || hint.syntheticException;

  /**
   * Some exceptions from Sentry's Hint object will be a string named 'undefined',
   * instead of being an undefined object.
   */
  if (exception && exception !== 'undefined') {
    const castedError = exception as Error;
    const typeguard = castedError?.name && castedError?.message ? castedError : null;
    return typeguard;
  }

  return null;
};

const beforeSendEventProduction = (event: ErrorEvent, hint: EventHint) => {
  const exception = getExceptionMaybe(hint);

  // BabylonJS logging messages when it crashes
  if (exception?.message.startsWith('BJS ')) {
    event.fingerprint = ['BabylonJS-logging'];
    event.level = 'log';
  }

  // InsightsAPI that fails internally
  if (exception?.message.endsWith('TypeError: Failed to fetch')) {
    event.fingerprint = ['InsightsAPI - Failed to fetch'];
    event.level = 'log';
  }

  // InsightsAPI that fails internally
  if (exception?.message.endsWith('TypeError: Load failed')) {
    event.fingerprint = ['InsightsAPI - Load failed'];
    event.level = 'log';
  }

  return event;
};

const beforeSendEventLocal = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
  if (isVerboseSentry()) {
    // eslint-disable-next-line no-console
    console.log(event, hint);
    return event;
  }

  if (event.level === 'error' && event.logger !== 'console') {
    console.error(getExceptionMaybe(hint));
  }

  return null;
};

const beforeSendEventStage = (event: ErrorEvent, hint: EventHint) => {
  // eslint-disable-next-line no-console
  console.log(event, hint);

  return event;
};

export const setBeforeSendCallback = (
  environment: typeof E2E_TEST_ENVIRONMENT | EnvironmentEnum
) => {
  switch (environment) {
    case EnvironmentEnum.PRODUCTION:
      return beforeSendEventProduction;
    case EnvironmentEnum.STAGE:
      return beforeSendEventStage;
    case EnvironmentEnum.LOCAL:
      return beforeSendEventLocal;
    default:
      return undefined;
  }
};

const beforeSendTransactionEventStage = (event: TransactionEvent, hint: EventHint) => {
  // eslint-disable-next-line no-console
  console.log(event, hint);

  return event;
};

const beforeSendTransactionEventLocal = (event: TransactionEvent, hint: EventHint) => {
  if (isVerboseSentry()) {
    // eslint-disable-next-line no-console
    console.log(event, hint);
  }

  return event;
};

export const setBeforeSendTransactionCallback = (
  environment: typeof E2E_TEST_ENVIRONMENT | EnvironmentEnum
) => {
  switch (environment) {
    case EnvironmentEnum.STAGE:
      return beforeSendTransactionEventStage;
    case EnvironmentEnum.LOCAL:
      return beforeSendTransactionEventLocal;
    default:
      return undefined;
  }
};

export const setInitialScope = async (
  environment: typeof E2E_TEST_ENVIRONMENT | EnvironmentEnum
) => {
  const scope = getGlobalScope();
  const locale = scope.getScopeData().tags?.locale;
  const filteredLocale = locale ? locale.toString() : '';

  if (unsupportedLocales.includes(filteredLocale)) {
    // Note that this sets the level for all events including span, transaction and errors.
    // Unsupported locales are outliers and transactions from those can be ignored
    // until we've found a better solution to filter out errors specifically
    scope.setLevel('warning');
  }

  if (environment === E2E_TEST_ENVIRONMENT) {
    scope.setLevel('log');
  }
};

export const tracesSampleRate = () => {
  if (isVerboseSentry()) {
    return 1;
  }

  if (isKiosk()) {
    return TracesSampleRateEnum.KIOSK;
  }

  if (isE2eTest()) {
    return TracesSampleRateEnum.E2ETEST;
  }

  return TracesSampleRateEnum.DEFAULT;
};

export const replaysOnErrorSampleRate = () => {
  if (isVerboseSentry()) {
    return 1;
  }

  if (isKiosk()) {
    return ReplaysOnErrorSampleRateEnum.KIOSK;
  }

  // No replays on E2E since they are already recorded on SauceLabs
  if (isE2eTest()) {
    return ReplaysOnErrorSampleRateEnum.E2ETEST;
  }

  return ReplaysOnErrorSampleRateEnum.DEFAULT;
};

export const replaysSessionSampleRate = () => {
  if (isVerboseSentry()) {
    return 1;
  }

  if (isKiosk()) {
    return ReplaysSessionSampleRateEnum.KIOSK;
  }

  return ReplaysSessionSampleRateEnum.DEFAULT;
};
